import { Typography, Box, useTheme } from '@mui/material';
import { TextInput, required, maxLength, regex, useRecordContext, FunctionField, SelectInput, useTranslate } from 'react-admin';
import { noSpecialCharacters, validNip } from '@/components/global/validators/customValidators.jsx';
import { styled } from '@mui/material/styles';
import ContractorLogoInput from '@/components/contractors/ContractorLogoInput.jsx';
import { forwarderTypeChoices } from '@/components/global/options/selectOptions.jsx';

const validateCode = regex(/^\d{3}$/, 'Must be a valid code number - 3 digits.');

const StyledContractorEditBasicInfo = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
    width: '100%',
    marginBottom: '40px',
    [theme.breakpoints.down('md')]: {
        gap: '24px',
    },
    '& .MuiTypography-root': {
        marginBottom: '0',
        fontSize: '22px',
        lineHeight: '28px',
    },
    '& .MuiFormHelperText-root': {
        display: 'none',
        '&.Mui-error': {
            display: 'block',
        },
    },
    '& .created-at-info': {
        fontSize: '11px',
        lineHeight: '16px',
        letterSpacing: '-0.11px',
        fontWeight: 400,
    },
}));

export const ContractorEditBasicInfo = ({ action = 'edit' }) => {
    const translate = useTranslate();
    const record = useRecordContext();
    const theme = useTheme();
    const validateLogo = action === 'create' || !record?.currentLogo ? required(translate('common.validation.required')) : null;

    return (
        <StyledContractorEditBasicInfo>
            <Typography variant='h5' gutterBottom>
                {translate('resources.product.labels.basicInfo')}
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr',
                    gap: '32px',
                    width: '100%',
                    [theme.breakpoints.down('md')]: { gridTemplateColumns: '1fr', gap: '24px' },
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px', [theme.breakpoints.down('md')]: { gap: '24px' } }}>
                    <TextInput
                        source='name'
                        label={translate('resources.contractor.labels.contractorName')}
                        validate={[
                            required(translate('common.validation.required')),
                            maxLength(150, translate('common.validation.maxLength', { count: 150 })),
                            noSpecialCharacters,
                        ]}
                        fullWidth
                    />
                    <TextInput
                        source='nip'
                        label={translate('resources.contractor.fields.nip')}
                        validate={[required(translate('common.validation.required')), validNip]}
                        fullWidth
                    />
                    <SelectInput
                        source='forwarderType'
                        label={translate('resources.contractor.labels.forwarderType')}
                        validate={required(translate('common.validation.required'))}
                        choices={forwarderTypeChoices(translate)}
                        defaultValue='Default'
                        fullWidth
                        emptyValue='Default'
                        emptyText={translate('common.all')}
                    />
                </Box>
                <TextInput source='code' label={translate('resources.contractor.labels.contractorCode')} validate={validateCode} fullWidth />
            </Box>

            <ContractorLogoInput
                source='logo'
                record={record}
                validate={validateLogo}
                instructionText={translate('resources.contractor.validation.fileFormats', { formats: 'png, jpg, svg', maxSize: 150 })}
            />

            <FunctionField
                className='created-at-info'
                source='record'
                render={record => {
                    return (
                        <>
                            {translate('resources.contractor.labels.createdAt', {
                                createdAt: new Date(record.createdAt).toLocaleDateString(),
                                updatedAt: `${new Date(record.updatedAt).toLocaleDateString()}, ${new Date(record.updatedAt).toLocaleTimeString()}`,
                            })}
                        </>
                    );
                }}
            />
        </StyledContractorEditBasicInfo>
    );
};
